import { h, Component, ComponentChildren, createContext } from 'preact'
import { Store } from 'redux'
import { AppState } from '../v1/store/state'
import { AppAction } from '../v1/store/actions'
import { useContext } from 'preact/hooks'
import { AppStore } from '../v1/store'

type Props = {
  store: Store<AppState, AppAction>
  children: ComponentChildren
}

type State = {
  store: Store<AppState, AppAction>
  appState: AppState
}

const ReduxContext = createContext<State | null>(null)

export class ReduxProvider extends Component<Props, State> {
  unsubscribe: () => void

  constructor(props: Props) {
    super()
    const { store } = props
    this.state = { store, appState: store.getState() }
  }

  updateState = currentState => {
    const store = this.props.store
    const appState = store.getState()
    if (currentState.appState === appState) {
      return currentState
    }
    return { store, appState }
  }

  subscribe() {
    this.unsubscribe = this.props.store.subscribe(() => this.setState(this.updateState))
  }

  componentDidMount(): void {
    this.setState(this.updateState)

    this.subscribe()
  }

  componentDidUpdate(prevProps) {
    if (this.props.store !== prevProps.store) {
      if (this.unsubscribe) this.unsubscribe()

      this.subscribe()
    }
  }

  componentWillUnmount(): void {
    this.unsubscribe()
  }

  render() {
    return <ReduxContext.Provider value={this.state}>{this.props.children}</ReduxContext.Provider>
  }
}

export function useAppState(): AppState {
  const ctx = useContext(ReduxContext)

  if (ctx && ctx.appState) {
    return ctx.appState
  }

  throw Error('No appState in context')
}

export function useStore(): AppStore {
  const ctx = useContext(ReduxContext)

  if (ctx && ctx.store) {
    return ctx.store
  }

  throw Error('No store present in context')
}
